import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useDialogs } from "@/components/GlobalDialogs";
import { getBlogPost } from "@/lib/api";

export default function BlogDetail() {
  const { slug } = useParams();
  const { openJoin } = useDialogs();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="ytca-container py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Post not found</h1>
      <Button asChild className="mt-6 rounded-full"><Link to="/blog">Back to Blog</Link></Button>
    </div>
  );
  if (!post) return <div className="ytca-container py-24 text-muted-foreground">Loading…</div>;

  return (
    <article>
      <div className="ytca-container max-w-3xl py-10">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="blog-back-link"><ArrowLeft className="h-4 w-4" />All posts</Link>
        <Badge variant="secondary" className="mt-6 rounded-full">{post.category}</Badge>
        <h1 className="mt-4 font-display text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">{post.title}</h1>
        <div className="mt-4 text-sm text-muted-foreground">{post.author} · {post.date} · {post.read_time}</div>
      </div>

      <div className="ytca-container max-w-4xl">
        <div className="overflow-hidden rounded-3xl border border-border">
          <img src={post.image} alt={post.title} className="aspect-[16/9] w-full object-cover" />
        </div>
      </div>

      <div className="ytca-container max-w-3xl py-10">
        <div className="space-y-6 text-lg leading-relaxed text-foreground/85">
          {(post.content || []).map((para, i) => <p key={i}>{para}</p>)}
        </div>

        {post.gallery && post.gallery.length > 0 && (
          <div className="mt-12 grid gap-4 sm:grid-cols-2" data-testid="blog-gallery">
            {post.gallery.map((src, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-border bg-secondary/40">
                <img src={src} alt={`${post.title} — photo ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {post.press && post.press.length > 0 && (
          <div className="mt-12 rounded-3xl border border-border bg-secondary/40 p-8" data-testid="blog-press">
            <h3 className="font-display text-xl font-semibold">In the press</h3>
            <ul className="mt-4 space-y-3">
              {post.press.map((p, i) => (
                <li key={i}>
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`blog-press-link-${i}`}
                    className="inline-flex items-center gap-2 text-primary transition-colors hover:text-primary/80 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4 shrink-0" />
                    <span>{p.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-12 rounded-3xl border border-border bg-secondary/40 p-8 text-center">
          <h3 className="font-display text-2xl font-semibold">Like how we think?</h3>
          <p className="mt-2 text-muted-foreground">Join the movement for a dose of creative inspiration.</p>
          <Button onClick={openJoin} className="mt-5 rounded-full" data-testid="blog-join-button">Join YTCA</Button>
        </div>
      </div>
    </article>
  );
}
